import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { money, toCents, fmtDate } from '../money';
import { Field, Combo } from './FormFrame';
import BankLinks from './BankLinks';
import { Upload, Download, ArrowLeft, CheckCircle2, AlertTriangle, FileSpreadsheet,
  Banknote, Users, Building2, ListTree, Package, FileText, BookOpen, ReceiptText,
  FileDown, Printer, SlidersHorizontal, Paperclip, MessageSquare, ChevronDown } from 'lucide-react';

const Txt = ({ className = '', ...p }) => (
  <input {...p} className={`h-9 w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 text-sm text-slate-900 dark:text-slate-100 ${className}`} />
);
const Card = ({ children, className = '' }) => (
  <div className={`rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/40 p-5 ${className}`}>{children}</div>
);
const PageHead = ({ title, sub, onBack }) => (
  <div className="flex items-center gap-3 mb-5">
    {onBack && <button onClick={onBack} data-testid="tool-back" className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/10"><ArrowLeft className="h-5 w-5" /></button>}
    <div><h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{title}</h2>{sub && <p className="text-sm text-slate-500">{sub}</p>}</div>
  </div>
);
const errOf = (e) => e?.response?.data?.detail || 'Request failed';
const blobDownload = (data, name, type) => {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a'); a.href = url; a.download = name; a.click();
};

// ══ EXPORT DATA ════════════════════════════════════════════════════════════
const EXP_REPORTS = [['general_ledger', 'General ledger'], ['profit_loss', 'Profit and loss'],
  ['balance_sheet', 'Balance sheet'], ['trial_balance', 'Trial balance'], ['journal', 'Journal']];
const EXP_LISTS = [['customers', 'Customers'], ['employees', 'Employees'], ['vendors', 'Vendors'],
  ['chart_of_accounts', 'Chart of accounts'], ['products_services', 'Products and services']];
const PRESETS = [['all', 'All dates'], ['this_fiscal_year_to_date', 'This fiscal year-to-date'],
  ['last_fiscal_year', 'Last fiscal year'], ['this_month', 'This month'], ['custom', 'Custom']];

export function ExportData({ base, onBack }) {
  const [tab, setTab] = useState('reports');
  const [reps, setReps] = useState(['profit_loss', 'balance_sheet']);
  const [lists, setLists] = useState(['customers', 'chart_of_accounts']);
  const [preset, setPreset] = useState('all');
  const [start, setStart] = useState(''); const [end, setEnd] = useState('');
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState('');
  const toggle = (arr, set, k) => set(arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]);
  const run = async () => {
    setBusy(true); setMsg('');
    try {
      const params = preset === 'custom' ? { preset: 'custom', start, end } : { preset };
      const r = await axios.post(`${base}/export/data`, { report_keys: reps, list_keys: lists, params }, { responseType: 'blob' });
      blobDownload(r.data, 'olfin_export.zip', 'application/zip');
      setMsg('Export downloaded.');
    } catch (e) { setMsg(errOf(e)); } finally { setBusy(false); }
  };
  const Chk = ({ k, label, arr, set }) => (
    <label className="flex items-center gap-2 py-1.5 text-sm cursor-pointer" data-testid={`exp-${k}`}>
      <input type="checkbox" checked={arr.includes(k)} onChange={() => toggle(arr, set, k)} /> {label}
    </label>
  );
  return (
    <div className="p-6 max-w-3xl" data-testid="export-data-page">
      <PageHead title="Export data" sub="Download your reports and lists as one .zip of Excel files." onBack={onBack} />
      <div className="flex gap-2 mb-4">
        {[['reports', 'Reports'], ['lists', 'Lists']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`exp-tab-${k}`}
            className={`px-4 py-2 rounded-lg text-sm ${tab === k ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-300'}`}>{l}</button>
        ))}
      </div>
      <Card>
        {tab === 'reports' ? (
          <>
            <div className="grid sm:grid-cols-2 gap-x-6">{EXP_REPORTS.map(([k, l]) => <Chk key={k} k={k} label={l} arr={reps} set={setReps} />)}</div>
            <div className="mt-4 grid sm:grid-cols-3 gap-3">
              <Field label="Date range"><Combo value={preset} onChange={setPreset} options={PRESETS.map(([id, label]) => ({ id, label }))} testid="exp-preset" /></Field>
              {preset === 'custom' && <Field label="From"><Txt type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="exp-from" /></Field>}
              {preset === 'custom' && <Field label="To"><Txt type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="exp-to" /></Field>}
            </div>
          </>
        ) : (
          <div className="grid sm:grid-cols-2 gap-x-6">{EXP_LISTS.map(([k, l]) => <Chk key={k} k={k} label={l} arr={lists} set={setLists} />)}</div>
        )}
      </Card>
      <div className="mt-4 flex items-center gap-3">
        <Button onClick={run} disabled={busy || (!reps.length && !lists.length)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="exp-run"><Download className="h-4 w-4 mr-1" />{busy ? 'Building…' : 'Export'}</Button>
        {msg && <span className="text-sm text-slate-300" data-testid="exp-msg">{msg}</span>}
      </div>
    </div>
  );
}

function DepositDetailImport({ base, onBack }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(null);

  const send = async (commit) => {
    if (!file) { setErr('Choose a Deposit Detail file first.'); return; }
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('commit', commit);
      const r = await axios.post(`${base}/import/deposit-detail`, fd);
      if (commit) { setDone(r.data); setPreview(null); } else setPreview(r.data);
    } catch (e) { setErr(errOf(e)); } finally { setBusy(false); }
  };
  const Stat = ({ label, value, tone = '' }) => (
    <div className="rounded-lg border border-slate-700 bg-slate-800/60 px-4 py-3">
      <div className={`text-2xl font-semibold ${tone}`}>{value}</div>
      <div className="text-xs text-slate-400">{label}</div>
    </div>
  );
  const res = done || preview;
  return (
    <div className="p-6 max-w-4xl" data-testid="deposit-detail-import">
      <PageHead title="Deposit detail (customer attribution)" onBack={onBack}
        sub="Match each legacy Deposit Detail line to its posted deposit line and set the customer. Amounts and accounts are never changed." />
      {err && <div className="mb-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300" data-testid="dep-detail-err">{err}</div>}
      <Card className="mb-4">
        <label className="block text-sm text-slate-300 mb-2">Deposit Detail export (.xlsx or .csv)</label>
        <input type="file" accept=".xlsx,.csv" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setDone(null); }}
          data-testid="dep-detail-file" className="text-sm text-slate-300" />
        <div className="mt-4 flex gap-2">
          <Button onClick={() => send(false)} disabled={busy || !file} data-testid="dep-detail-preview">
            <Upload className="h-4 w-4 mr-1" />{busy ? 'Working…' : 'Preview (dry run)'}</Button>
          <Button onClick={() => send(true)} disabled={busy || !preview} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dep-detail-commit">
            <CheckCircle2 className="h-4 w-4 mr-1" />Apply attribution</Button>
        </div>
      </Card>
      {res && (
        <Card data-testid="dep-detail-result">
          {done && <div className="mb-3 flex items-center gap-2 text-emerald-400 text-sm"><CheckCircle2 className="h-4 w-4" /> Attribution applied.</div>}
          {preview && <div className="mb-3 flex items-center gap-2 text-amber-400 text-sm"><AlertTriangle className="h-4 w-4" /> Preview only — nothing saved yet.</div>}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Stat label="Lines in file" value={res.rows} />
            <Stat label="Matched" value={res.matched} tone="text-emerald-400" />
            <Stat label="Unmatched" value={res.unmatched} tone={res.unmatched ? 'text-amber-400' : ''} />
            <Stat label="Ambiguous" value={res.ambiguous} tone={res.ambiguous ? 'text-amber-400' : ''} />
            <Stat label="New customers" value={res.created_customers?.count ?? 0} />
          </div>
          {!!(res.unmatched_samples || []).length && (
            <div className="mt-4">
              <div className="text-xs uppercase text-slate-400 mb-1">Unmatched sample</div>
              <table className="w-full text-sm"><tbody>
                {res.unmatched_samples.map((s, i) => (
                  <tr key={i} className="border-b border-slate-800"><td className="py-1 pr-3">{s.date}</td><td className="pr-3 text-slate-400">{s.account}</td><td className="pr-3 text-right tabular-nums">{money(Math.round(s.amount * 100))}</td><td className="text-slate-300">{s.customer}</td></tr>
                ))}
              </tbody></table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function ClearedStatusImport({ base, onBack }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(null);
  // manual reconciliation history
  const [accounts, setAccounts] = useState([]);
  const [acct, setAcct] = useState('');
  const [stmtDate, setStmtDate] = useState('');
  const [ending, setEnding] = useState('');
  const [recBusy, setRecBusy] = useState(false);
  const [recErr, setRecErr] = useState('');
  const [recDone, setRecDone] = useState(null);

  useEffect(() => {
    axios.get(`${base}/accounts`).then((r) => {
      const a = (r.data?.accounts || []).filter((x) => x.type === 'Bank' || x.type === 'Credit Card');
      setAccounts(a);
    }).catch(() => {});
  }, [base]);

  const send = async (commit) => {
    if (!file) { setErr('Choose a cleared-status file first.'); return; }
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('commit', commit);
      const r = await axios.post(`${base}/import/cleared-status`, fd);
      if (commit) { setDone(r.data); setPreview(null); } else setPreview(r.data);
    } catch (e) { setErr(errOf(e)); } finally { setBusy(false); }
  };
  const addHistory = async () => {
    if (!acct || !stmtDate) { setRecErr('Pick an account and a statement date.'); return; }
    setRecBusy(true); setRecErr(''); setRecDone(null);
    try {
      const r = await axios.post(`${base}/reconciliation-history`,
        { account_id: acct, statement_date: stmtDate, ending_balance: ending });
      setRecDone(r.data); setStmtDate(''); setEnding('');
    } catch (e) { setRecErr(errOf(e)); } finally { setRecBusy(false); }
  };
  const Stat = ({ label, value, tone = '' }) => (
    <div className="rounded-lg border border-slate-700 bg-slate-800/60 px-4 py-3">
      <div className={`text-2xl font-semibold ${tone}`}>{value}</div>
      <div className="text-xs text-slate-400">{label}</div>
    </div>
  );
  const res = done || preview;
  return (
    <div className="p-6 max-w-4xl" data-testid="cleared-status-import">
      <PageHead title="Import cleared status" onBack={onBack}
        sub="Upload a file of transactions with their bank status (R = reconciled, C = cleared) to mark the matching register lines. Record a reconciliation-history entry so Reconcile starts after it." />
      {err && <div className="mb-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300" data-testid="cleared-err">{err}</div>}
      <Card className="mb-4">
        <label className="block text-sm text-slate-300 mb-1">Cleared-status export (.csv or .xlsx)</label>
        <div className="text-xs text-slate-500 mb-2">Columns: date, num, payee, amount (deposit +, payment −), status (R / C / blank), account.</div>
        <input type="file" accept=".xlsx,.csv" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setDone(null); }}
          data-testid="cleared-file" className="text-sm text-slate-300" />
        <div className="mt-4 flex gap-2">
          <Button onClick={() => send(false)} disabled={busy || !file} data-testid="cleared-preview">
            <Upload className="h-4 w-4 mr-1" />{busy ? 'Working…' : 'Preview (dry run)'}</Button>
          <Button onClick={() => send(true)} disabled={busy || !preview} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cleared-commit">
            <CheckCircle2 className="h-4 w-4 mr-1" />Apply status</Button>
        </div>
      </Card>
      {res && (
        <Card data-testid="cleared-result">
          {done && <div className="mb-3 flex items-center gap-2 text-emerald-400 text-sm"><CheckCircle2 className="h-4 w-4" /> Status applied to {done.applied} lines.</div>}
          {preview && <div className="mb-3 flex items-center gap-2 text-amber-400 text-sm"><AlertTriangle className="h-4 w-4" /> Preview only — nothing saved yet.</div>}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Rows in file" value={res.rows} />
            <Stat label="Matched" value={res.matched} tone="text-emerald-400" />
            <Stat label="Unmatched" value={res.unmatched} tone={res.unmatched ? 'text-amber-400' : ''} />
            <Stat label="Ambiguous" value={res.ambiguous} tone={res.ambiguous ? 'text-amber-400' : ''} />
          </div>
          {!!(res.unmatched_samples || []).length && (
            <div className="mt-4">
              <div className="text-xs uppercase text-slate-400 mb-1">Unmatched sample</div>
              <table className="w-full text-sm"><tbody>
                {res.unmatched_samples.map((s, i) => (
                  <tr key={i} className="border-b border-slate-800"><td className="py-1 pr-3">{fmtDate(s.date)}</td><td className="pr-3 text-slate-400">{s.num}</td><td className="pr-3 text-slate-400">{s.account}</td><td className="pr-3 text-right tabular-nums">{money(Math.round((s.amount || 0) * 100))}</td><td className="text-slate-500">{s.reason}</td></tr>
                ))}
              </tbody></table>
            </div>
          )}
        </Card>
      )}
      <Card className="mt-6" data-testid="recon-history-form">
        <div className="font-medium text-slate-100 mb-1">Add reconciliation history</div>
        <div className="text-sm text-slate-400 mb-4">Record a past reconciliation (account, statement date, ending balance) so the register shows "Reconciled through" and Reconcile begins after that date.</div>
        {recErr && <div className="mb-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300" data-testid="recon-err">{recErr}</div>}
        {recDone && <div className="mb-3 flex items-center gap-2 text-emerald-400 text-sm" data-testid="recon-done"><CheckCircle2 className="h-4 w-4" /> Reconciliation history added for {fmtDate(recDone.statement_date)}.</div>}
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Account">
            <Combo value={acct} onChange={setAcct} testid="recon-account"
              options={accounts.map((a) => ({ id: a.account_id, label: a.display_name }))} />
          </Field>
          <Field label="Statement date">
            <input type="date" value={stmtDate} onChange={(e) => setStmtDate(e.target.value)} data-testid="recon-date"
              className="h-9 w-full rounded-lg border border-slate-700 bg-slate-900 px-2 text-sm text-slate-100" />
          </Field>
          <Field label="Ending balance">
            <input value={ending} onChange={(e) => setEnding(e.target.value)} placeholder="0.00" data-testid="recon-ending"
              className="h-9 w-full rounded-lg border border-slate-700 bg-slate-900 px-2 text-sm text-slate-100 tabular-nums" />
          </Field>
        </div>
        <div className="mt-4"><Button onClick={addHistory} disabled={recBusy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="recon-add">{recBusy ? 'Saving…' : 'Add reconciliation history'}</Button></div>
      </Card>
    </div>
  );
}

// ══ IMPORT DATA (tiles + 3-step) ═══════════════════════════════════════════
const TILE_ICON = { bank_data: Banknote, customers: Users, vendors: Building2, chart_of_accounts: ListTree,
  products_services: Package, invoices: FileText, credit_memos: ReceiptText, sales_receipts: ReceiptText,
  journal_entries: BookOpen, bills: FileText };

export function ImportData({ base, onBack }) {
  const [tiles, setTiles] = useState([]);
  const [active, setActive] = useState(null);
  const [depDetail, setDepDetail] = useState(false);
  const [cleared, setCleared] = useState(false);
  useEffect(() => { axios.get(`${base}/import/tiles`).then((r) => setTiles(r.data || [])).catch(() => {}); }, [base]);
  if (depDetail) return <DepositDetailImport base={base} onBack={() => setDepDetail(false)} />;
  if (cleared) return <ClearedStatusImport base={base} onBack={() => setCleared(false)} />;
  if (active) return active.key === 'bank_data'
    ? <BankImportFlow base={base} onBack={() => setActive(null)} />
    : <ImportFlow base={base} tile={active} onBack={() => setActive(null)} />;
  return (
    <div className="p-6" data-testid="import-data-page">
      <PageHead title="Import data" sub="Bring in bank data, lists and transactions from a CSV or Excel file." onBack={onBack} />
      <button onClick={() => setDepDetail(true)} data-testid="import-tile-deposit_detail"
        className="mb-4 w-full rounded-xl border border-emerald-600/40 bg-emerald-600/10 p-4 text-left hover:border-emerald-500 transition-colors">
        <div className="flex items-center gap-3">
          <ReceiptText className="h-7 w-7 text-emerald-400" />
          <div>
            <div className="font-medium text-slate-100">Deposit detail (customer attribution)</div>
            <div className="text-sm text-slate-400">Upload the legacy Deposit Detail export to tag batch-deposit lines with their customer. Preview first; amounts and accounts never change.</div>
          </div>
        </div>
      </button>
      <button onClick={() => setCleared(true)} data-testid="import-tile-cleared_status"
        className="mb-4 w-full rounded-xl border border-emerald-600/40 bg-emerald-600/10 p-4 text-left hover:border-emerald-500 transition-colors">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-7 w-7 text-emerald-400" />
          <div>
            <div className="font-medium text-slate-100">Import cleared status</div>
            <div className="text-sm text-slate-400">Mark register lines Reconciled (R) or Cleared (C) from a bank file, and record reconciliation history. Preview first.</div>
          </div>
        </div>
      </button>
      <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {tiles.map((t) => { const Icon = TILE_ICON[t.key] || FileSpreadsheet; return (
          <button key={t.key} onClick={() => setActive(t)} data-testid={`import-tile-${t.key}`}
            className="rounded-xl border border-slate-700 bg-slate-800/40 p-5 text-left hover:border-emerald-500 transition-colors">
            <Icon className="h-7 w-7 text-emerald-400 mb-3" />
            <div className="font-medium text-slate-100">{t.label}</div>
          </button>
        ); })}
      </div>
    </div>
  );
}

function ImportFlow({ base, tile, onBack }) {
  const [step, setStep] = useState(1);
  const [hasHeader, setHasHeader] = useState(true);
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [records, setRecords] = useState([]);
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  const sample = () => window.open(`${base}/import/tiles/${tile.key}/sample?fmt=csv`, '_blank');
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('file', f); fd.append('has_header', hasHeader);
      const r = await axios.post(`${base}/import/tiles/${tile.key}/parse`, fd);
      setParsed(r.data); setMapping(r.data.auto_map || {}); setStep(2);
    } catch (e2) { setErr(errOf(e2)); } finally { setBusy(false); }
  };
  const buildRecords = useCallback(() => (parsed?.rows || []).map((row) => {
    const rec = {}; tile.fields.forEach((f) => { if (mapping[f]) rec[f] = row[mapping[f]] ?? ''; }); return rec;
  }), [parsed, mapping, tile]);
  const goReview = () => { setRecords(buildRecords()); setStep(3); };
  const run = async () => {
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${base}/import/tiles/${tile.key}/run`, { records, options: { overwrite } });
      setResult(r.data); setStep(4);
    } catch (e2) { setErr(errOf(e2)); } finally { setBusy(false); }
  };
  const errSet = new Set((result?.errors || []).map((e) => e.row));
  const Stepper = () => (
    <div className="flex gap-2 mb-5 text-xs">{['Upload', 'Map fields', 'Review', 'Done'].map((l, i) => (
      <span key={l} className={`px-3 py-1 rounded-full ${step === i + 1 ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-400'}`}>{i + 1}. {l}</span>
    ))}</div>
  );
  return (
    <div className="p-6 max-w-5xl" data-testid={`import-flow-${tile.key}`}>
      <PageHead title={`Import ${tile.label}`} onBack={onBack} />
      <Stepper />
      {err && <div className="mb-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300" data-testid="import-err">{err}</div>}
      {step === 1 && (
        <Card>
          <label className="flex items-center gap-2 mb-4 text-sm"><input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} data-testid="import-hasheader" /> My file has a header row</label>
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm cursor-pointer" data-testid="import-upload">
              <Upload className="h-4 w-4" /> Choose CSV / Excel<input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} />
            </label>
            <button onClick={sample} className="text-sm text-emerald-400 hover:underline" data-testid="import-sample"><Download className="h-4 w-4 inline mr-1" />Download a sample file</button>
          </div>
        </Card>
      )}
      {step === 2 && parsed && (
        <Card>
          <p className="text-sm text-slate-400 mb-3">Match each field to a column in your file. We auto-matched by name.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {tile.fields.map((f) => (
              <Field key={f} label={f}>
                <Combo value={mapping[f] || ''} onChange={(v) => setMapping({ ...mapping, [f]: v })}
                  options={[{ id: '', label: '— skip —' }, ...parsed.headers.map((h) => ({ id: h, label: h }))]} testid={`map-${f}`} />
              </Field>
            ))}
          </div>
          <div className="mt-4 flex gap-2"><Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={goReview} data-testid="import-to-review">Review {parsed.rows.length} rows</Button></div>
        </Card>
      )}
      {step === 3 && (
        <Card>
          {tile.kind === 'list' && <label className="flex items-center gap-2 mb-3 text-sm"><input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="import-overwrite" /> Overwrite existing records with the same name</label>}
          <div className="overflow-auto max-h-[52vh] rounded-lg border border-slate-700">
            <table className="w-full text-xs"><thead className="bg-slate-800 sticky top-0"><tr><th className="p-2 text-left text-slate-400">#</th>{tile.fields.map((f) => <th key={f} className="p-2 text-left text-slate-400">{f}</th>)}</tr></thead>
              <tbody data-testid="import-review-grid">{records.slice(0, 300).map((rec, i) => (
                <tr key={i} className="border-t border-slate-800">
                  <td className="p-1 text-slate-500">{i + 1}</td>
                  {tile.fields.map((f) => (
                    <td key={f} className="p-1"><input value={rec[f] ?? ''} onChange={(e) => { const n = [...records]; n[i] = { ...n[i], [f]: e.target.value }; setRecords(n); }}
                      className="h-7 w-full min-w-[90px] rounded border border-slate-700 bg-slate-900 px-1.5 text-slate-100" /></td>
                  ))}
                </tr>
              ))}</tbody></table>
          </div>
          <div className="mt-4 flex gap-2"><Button variant="ghost" onClick={() => setStep(2)}>Back</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={run} disabled={busy} data-testid="import-run">{busy ? 'Importing…' : `Import ${records.length} rows`}</Button></div>
        </Card>
      )}
      {step === 4 && result && (
        <Card>
          <div className="flex items-center gap-2 text-emerald-400 mb-3"><CheckCircle2 className="h-6 w-6" /><span className="text-lg font-semibold">Import complete</span></div>
          <div className="grid grid-cols-4 gap-3 text-center" data-testid="import-summary">
            {[['Created', result.created], ['Updated', result.updated], ['Skipped', result.skipped], ['Errors', result.error_count]].map(([l, v]) => (
              <div key={l} className="rounded-lg bg-slate-800 p-3"><div className="text-2xl font-bold text-slate-100">{v}</div><div className="text-xs text-slate-400">{l}</div></div>
            ))}
          </div>
          {result.error_count > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2"><span className="text-sm text-amber-400"><AlertTriangle className="h-4 w-4 inline mr-1" />{result.error_count} rows had problems</span>
                <button onClick={() => { const hdr = ['row', 'reason']; const csv = [hdr.join(','), ...result.errors.map((e) => `"${e.row}","${(e.reason || '').replace(/"/g, '""')}"`)].join('\n'); blobDownload(csv, 'import_errors.csv', 'text/csv'); }} className="text-xs text-emerald-400 hover:underline" data-testid="import-err-csv">Download error rows (CSV)</button></div>
              <div className="max-h-48 overflow-auto rounded-lg border border-slate-700 text-xs">{result.errors.map((e, i) => (
                <div key={i} className="px-3 py-1.5 border-b border-slate-800"><span className="text-slate-400">Row {e.row}:</span> <span className="text-amber-300">{e.reason}</span></div>
              ))}</div>
            </div>
          )}
          <div className="mt-4"><Button onClick={onBack} className="bg-emerald-600 hover:bg-emerald-700" data-testid="import-done">Done</Button></div>
        </Card>
      )}
    </div>
  );
}

function BankImportFlow({ base, onBack }) {
  const [accounts, setAccounts] = useState([]);
  const [acct, setAcct] = useState(''); const [dateFmt, setDateFmt] = useState('MM/DD/YYYY');
  const [amountMode, setAmountMode] = useState('single');
  const [parsed, setParsed] = useState(null);
  const [file, setFile] = useState(null); const [headers, setHeaders] = useState([]);
  const [map, setMap] = useState({ date: '', description: '', amount: '', spent: '', received: '' });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(''); const [result, setResult] = useState(null);
  useEffect(() => { axios.get(`${base}/accounts`).then((r) => setAccounts((r.data?.accounts || []).filter((a) => ['Bank', 'Credit Card'].includes(a.type) && a.is_active))).catch(() => {}); }, [base]);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return; setFile(f); setErr('');
    const txt = await f.text(); const first = txt.split(/\r?\n/)[0]; setHeaders(first.split(',').map((h) => h.replace(/^\uFEFF/, '').trim().replace(/^"(.*)"$/, '$1')));
  };
  const parse = async () => {
    if (!file) return; setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('date_format', dateFmt); fd.append('has_header', true);
      const mp = amountMode === 'single' ? { date: map.date, description: map.description, amount: map.amount } : { date: map.date, description: map.description, spent: map.spent, received: map.received };
      fd.append('mapping', JSON.stringify(mp));
      const r = await axios.post(`${base}/bank-feeds/parse`, fd); setParsed(r.data);
    } catch (e2) { setErr(errOf(e2)); } finally { setBusy(false); }
  };
  const doImport = async () => {
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${base}/bank-feeds/import`, { account_id: acct, rows: parsed.rows }); setResult(r.data);
    } catch (e2) { setErr(errOf(e2)); } finally { setBusy(false); }
  };
  const hopts = [{ id: '', label: '— choose —' }, ...headers.map((h) => ({ id: h, label: h }))];
  return (
    <div className="p-6 max-w-3xl" data-testid="import-flow-bank_data">
      <PageHead title="Import bank data" sub="Imported rows land in Bank transactions ▸ For review — never straight into the books." onBack={onBack} />
      {err && <div className="mb-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300">{err}</div>}
      {!result ? (
        <Card>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Bank or credit card account"><Combo value={acct} onChange={setAcct} options={accounts.map((a) => ({ id: a.account_id, label: a.full_name || a.name }))} testid="bank-acct" /></Field>
            <Field label="Date format"><Combo value={dateFmt} onChange={setDateFmt} options={['MM/DD/YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY', 'MM-DD-YYYY'].map((f) => ({ id: f, label: f }))} testid="bank-datefmt" /></Field>
          </div>
          <label className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm cursor-pointer" data-testid="bank-file">
            <Upload className="h-4 w-4" /> Choose CSV / .QBO / .QFX / .OFX<input type="file" accept=".csv,.ofx,.qfx,.qbo" className="hidden" onChange={onFile} />
          </label>
          {headers.length > 0 && (
            <div className="mt-4">
              <div className="flex gap-2 mb-3 text-sm">{[['single', 'One amount column'], ['split', 'Spent + Received']].map(([k, l]) => (
                <button key={k} onClick={() => setAmountMode(k)} className={`px-3 py-1.5 rounded-lg ${amountMode === k ? 'bg-emerald-600 text-white' : 'bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300'}`} data-testid={`bank-mode-${k}`}>{l}</button>))}</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Date column"><Combo value={map.date} onChange={(v) => setMap({ ...map, date: v })} options={hopts} testid="bank-map-date" /></Field>
                <Field label="Description column"><Combo value={map.description} onChange={(v) => setMap({ ...map, description: v })} options={hopts} testid="bank-map-desc" /></Field>
                {amountMode === 'single' ? <Field label="Amount column"><Combo value={map.amount} onChange={(v) => setMap({ ...map, amount: v })} options={hopts} testid="bank-map-amount" /></Field> : <>
                  <Field label="Spent column"><Combo value={map.spent} onChange={(v) => setMap({ ...map, spent: v })} options={hopts} testid="bank-map-spent" /></Field>
                  <Field label="Received column"><Combo value={map.received} onChange={(v) => setMap({ ...map, received: v })} options={hopts} testid="bank-map-received" /></Field>
                </>}
              </div>
              <Button onClick={parse} disabled={busy} variant="outline" className="mt-3" data-testid="bank-parse">{busy ? 'Reading…' : 'Preview'}</Button>
            </div>
          )}
          {parsed && (
            <div className="mt-4">
              <p className="text-sm text-slate-500 mb-2">{parsed.count} rows found. First few:</p>
              <div className="max-h-48 overflow-auto rounded-lg border border-slate-200 dark:border-slate-700 text-xs">
                <table className="w-full text-slate-900 dark:text-slate-100">
                  <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0"><tr className="text-slate-500">
                    <th className="p-2 text-left">Date</th><th className="p-2 text-left">Description</th>
                    <th className="p-2 text-right">Spent</th><th className="p-2 text-right">Received</th>
                    <th className="p-2 text-left">From/To</th><th className="p-2 text-left">Match/Categorize</th></tr></thead>
                  <tbody>{parsed.rows.slice(0, 8).map((r, i) => (
                    <tr key={i} className="border-t border-slate-200 dark:border-slate-800">
                      <td className="p-1.5 whitespace-nowrap">{fmtDate(r.date)}</td><td className="p-1.5">{r.description}</td>
                      <td className="p-1.5 text-right">{r.spent ? money(r.spent) : ''}</td>
                      <td className="p-1.5 text-right">{r.received ? money(r.received) : ''}</td>
                      <td className="p-1.5">{r.payee || ''}</td><td className="p-1.5">{r.category || ''}</td>
                    </tr>))}</tbody>
                </table></div>
              <Button onClick={doImport} disabled={busy || !acct} className="mt-3 bg-emerald-600 hover:bg-emerald-700" data-testid="bank-import">{busy ? 'Importing…' : `Import to For review`}</Button>
            </div>
          )}
        </Card>
      ) : (
        <Card>
          <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 mb-3"><CheckCircle2 className="h-6 w-6" /><span className="text-lg font-semibold">Added to For review</span></div>
          <div className="grid grid-cols-3 gap-3 text-center" data-testid="bank-import-summary">
            {[['Imported', result.imported], ['Skipped (dupes)', result.skipped], ['Total', result.total]].map(([l, v]) => (
              <div key={l} className="rounded-lg bg-slate-100 dark:bg-slate-800 p-3"><div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{v}</div><div className="text-xs text-slate-500">{l}</div></div>))}
          </div>
          {result.balance && <p className="mt-3 text-sm text-slate-600 dark:text-slate-300" data-testid="bank-import-balance">Bank balance rolled forward {money(result.balance.was)} → <b>{money(result.balance.amount)}</b> as of {fmtDate(result.balance.as_of)} — check it against the bank and correct it on Bank transactions if needed.</p>}
          <div className="mt-4"><Button onClick={onBack} className="bg-emerald-600 hover:bg-emerald-700">Done</Button></div>
        </Card>
      )}
    </div>
  );
}

// ══ BANK TRANSACTIONS — For review ═══════════════════════════════════════════
export function BankReview({ base, onBack, acct: acctProp, onAcct, onRegister }) {
  const [cards, setCards] = useState([]);
  const [acctState, setAcctState] = useState('');
  const acct = acctProp !== undefined ? acctProp : acctState;
  const setAcct = onAcct || setAcctState;
  const [tab, setTab] = useState('review');
  const [data, setData] = useState({ rows: [], total: 0, ready_count: 0, ready_threshold: 0.8 });
  const [search, setSearch] = useState(''); const [pageSize, setPageSize] = useState(50); const [page, setPage] = useState(1);
  const [accounts, setAccounts] = useState([]); const [names, setNames] = useState([]);
  const [sel, setSel] = useState({}); const [draft, setDraft] = useState({});
  const [matchRow, setMatchRow] = useState(null); const [cands, setCands] = useState([]);
  const [showRules, setShowRules] = useState(false); const [busy, setBusy] = useState(false);
  const [editP, setEditP] = useState({}); const [editC, setEditC] = useState({});
  const [datePreset, setDatePreset] = useState('all'); const [statusFilter, setStatusFilter] = useState('all');
  const [rowMenu, setRowMenu] = useState(''); const [rowMode, setRowMode] = useState({});
  const loadCards = useCallback(() => axios.get(`${base}/bank-review/accounts`).then((r) => {
    const list = r.data || [];
    setCards(list);
    if (!acct && list.length) {
      const byReview = [...list].sort((a, b) => (b.to_review || 0) - (a.to_review || 0));
      const best = byReview.find((c) => (c.to_review || 0) > 0)
        || list.find((c) => (c.feed_balance || 0) !== 0 || (c.posted_balance || 0) !== 0)
        || list[0];
      setAcct(best.account_id);
    }
  }).catch(() => {}), [base, acct]);
  const dateRange = () => {
    const now = new Date(); const iso = (d) => d.toISOString().slice(0, 10);
    if (datePreset === 'thismonth') return [iso(new Date(now.getFullYear(), now.getMonth(), 1)), iso(now)];
    if (datePreset === 'thisyear') return [iso(new Date(now.getFullYear(), 0, 1)), iso(now)];
    if (datePreset === 'last90') { const d = new Date(now); d.setDate(d.getDate() - 90); return [iso(d), iso(now)]; }
    return [undefined, undefined];
  };
  const load = useCallback(() => {
    if (!acct) return;
    const [df, dt] = dateRange();
    axios.get(`${base}/bank-review`, { params: { account_id: acct, status: tab, search: search || undefined, date_from: df, date_to: dt, filt: tab === 'review' ? statusFilter : undefined, page, page_size: pageSize } })
      .then((r) => {
        const d = r.data || { rows: [] };
        setData(d);
        const dr = {};
        (d.rows || []).forEach((row) => {
          const s = row.suggestion || {};
          // Pre-fill the category/payee from rule, import (legacy's own From/To +
          // Match/Categorize) AND history suggestions so the treasurer posts in one click.
          // Only 'rule' counts toward "ready to post"; match is picked via the Match modal.
          const canPrefill = ['rule', 'import', 'history'].includes(s.source);
          dr[row.row_id] = {
            payee_id: canPrefill ? (s.payee_id || '') : '',
            account_id: (canPrefill && s.source !== 'match') ? (s.account_id || '') : '',
            kind: s.kind && s.kind !== 'match' ? s.kind : (row.received ? 'deposit' : 'expense'),
          };
        });
        setDraft(dr);
      }).catch(() => {});
  }, [base, acct, tab, search, page, pageSize, datePreset, statusFilter]);
  useEffect(() => { loadCards(); axios.get(`${base}/accounts`).then((r) => setAccounts((r.data?.accounts || []).filter((a) => a.is_active))).catch(() => {}); axios.get(`${base}/lists/names`).then((r) => setNames(r.data || [])).catch(() => {}); }, [base]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); loadCards(); };
  const act = async (fn) => { try { await fn; refresh(); } catch (e) { alert(errOf(e)); } };
  const openMatch = async (row) => { setMatchRow(row); const r = await axios.get(`${base}/bank-review/${row.row_id}/match-candidates`); setCands(r.data.candidates || []); };
  const acctLabel = (a) => `${a.number ? `${a.number} ` : ''}${a.full_name || a.display_name || a.name}`;
  const acctOpts = accounts.map((a) => ({ id: a.account_id, label: acctLabel(a) }));
  const payeeOpts = names.map((n) => ({ id: n.name_id, label: n.name }));
  const acctName = Object.fromEntries(accounts.map((a) => [a.account_id, acctLabel(a)]));
  const payeeName = Object.fromEntries(names.map((n) => [n.name_id, n.name]));
  const addPayee = async (name) => { try { const r = await axios.post(`${base}/lists/names`, { name, name_type: 'vendor' }); setNames((p) => [...p, r.data]); return r.data.name_id; } catch (e) { alert(errOf(e)); return null; } };
  const setD = (rid, k, v) => setDraft((p) => ({ ...p, [rid]: { ...p[rid], [k]: v } }));
  const selIds = Object.keys(sel).filter((k) => sel[k]);
  const postRow = (row) => {
    const d = draft[row.row_id] || {};
    return act(axios.post(`${base}/bank-review/${row.row_id}/categorize`, { kind: d.kind || 'expense', account_id: d.account_id || null, payee_id: d.payee_id || null }));
  };
  const postSelected = async () => {
    const items = selIds.map((rid) => ({ row_id: rid, ...(draft[rid] || {}) })).filter((it) => it.account_id);
    if (items.length === 0) { alert('Choose a category for the selected rows first.'); return; }
    setBusy(true);
    try { const r = await axios.post(`${base}/bank-review/post-batch`, { items }); if (r.data.errors?.length) alert(`${r.data.done} posted · ${r.data.errors.length} skipped`); setSel({}); refresh(); }
    catch (e) { alert(errOf(e)); } finally { setBusy(false); }
  };
  const doRecompute = async () => { setBusy(true); try { await axios.post(`${base}/bank-review/recompute`, { account_id: acct || null }); refresh(); } catch (e) { alert(errOf(e)); } finally { setBusy(false); } };
  const addComment = async (row) => { const t = window.prompt('Comment', row.comment || ''); if (t === null) return; await axios.patch(`${base}/bank-review/${row.row_id}/comment`, { comment: t }).catch((e) => alert(errOf(e))); refresh(); };
  const addAttachment = async (row, file) => { if (!file) return; const fd = new FormData(); fd.append('file', file); await axios.post(`${base}/bank-review/${row.row_id}/attachment`, fd).catch((e) => alert(errOf(e))); refresh(); };
  const exportCsv = () => { const [df, dt] = dateRange(); const p = new URLSearchParams({ account_id: acct, status: tab, ...(df ? { date_from: df } : {}), ...(dt ? { date_to: dt } : {}), page_size: '300' }); window.open(`${base}/bank-review?${p.toString()}`, '_blank'); };
  const first = data.total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, data.total);
  const pages = Math.max(1, Math.ceil(data.total / pageSize));
  return (
    <div className="p-6" data-testid="bank-review-page">
      <PageHead title="Bank transactions" sub="Review, categorize and match imported bank activity before it hits the books." onBack={onBack} />
      <BankLinks base={base} onSynced={refresh} />
      <div className="flex gap-3 overflow-auto pb-3 mb-4">
        {cards.map((c) => (
          <button key={c.account_id} onClick={() => { setAcct(c.account_id); setPage(1); }} data-testid={`bank-card-${c.account_id}`}
            className={`shrink-0 w-56 rounded-xl border p-4 text-left ${acct === c.account_id ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-600/10' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/40'}`}>
            <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{c.name}</div>
            <div className="text-xs text-slate-500 mt-1">Bank balance {c.statement_balance != null ? money(c.statement_balance) : <span className="text-slate-400">—</span>}</div>
            <div className="text-xs text-slate-500">In OL Finance {money(c.posted_balance)}</div>
            {c.to_review > 0 && <div className="mt-2 inline-block text-xs bg-emerald-600 text-white rounded-full px-2 py-0.5">{c.to_review} to review</div>}
          </button>
        ))}
      </div>
      {tab === 'review' && data.ready_count > 0 && (
        <div className="mb-3 rounded-lg border border-emerald-200 dark:border-emerald-600/40 bg-emerald-50 dark:bg-emerald-600/10 p-3 text-sm text-emerald-800 dark:text-emerald-300 flex items-start gap-2" data-testid="bank-ready-banner">
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{data.ready_count} transaction{data.ready_count === 1 ? ' is' : 's are'} ready to post — These are high confidence suggestions for transactions based on your company history. Just review and post.</span>
        </div>
      )}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {[['review', 'For review'], ['categorized', 'Categorized'], ['excluded', 'Excluded']].map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); setPage(1); }} data-testid={`bank-tab-${k}`} className={`px-3 py-1.5 rounded-lg text-sm ${tab === k ? 'bg-emerald-600 text-white' : 'bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300'}`}>{l}</button>
        ))}
        <div className="flex-1" />
        <Txt placeholder="Search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="w-44" data-testid="bank-search" />
        <Combo value={datePreset} onChange={(v) => { setDatePreset(v); setPage(1); }} options={[['all', 'All dates'], ['thismonth', 'This month'], ['last90', 'Last 90 days'], ['thisyear', 'This year']].map(([id, label]) => ({ id, label }))} testid="bank-datefilter" />
        {tab === 'review' && (
          <Combo value={statusFilter} onChange={(v) => { setStatusFilter(v); setPage(1); }} testid="bank-statusfilter"
            options={[['all', `All transactions (${data.counts?.all ?? 0})`], ['recognized', `Recognized (${data.counts?.recognized ?? 0})`], ['ready', `Ready to post (${data.counts?.ready ?? 0})`], ['needs_review', `Needs review (${data.counts?.needs_review ?? 0})`]].map(([id, label]) => ({ id, label }))} />
        )}
        <Combo value={String(pageSize)} onChange={(v) => setPageSize(Number(v))} options={[50, 75, 100, 200, 300].map((n) => ({ id: String(n), label: `${n} / page` }))} testid="bank-pagesize" />
        <Button variant="outline" onClick={doRecompute} disabled={busy} data-testid="bank-recompute">Refresh suggestions</Button>
        <Button variant="outline" onClick={exportCsv} data-testid="bank-export" title="Export"><FileDown className="h-4 w-4" /></Button>
        <Button variant="outline" onClick={() => window.print()} data-testid="bank-print" title="Print"><Printer className="h-4 w-4" /></Button>
        <Button variant="outline" onClick={() => setShowRules(true)} data-testid="bank-rules-open" title="Rules"><SlidersHorizontal className="h-4 w-4" /></Button>
        {onRegister && <button onClick={onRegister} className="text-sm text-emerald-600 dark:text-emerald-400 hover:underline" data-testid="bank-go-register">Go to bank register</button>}
      </div>
      {selIds.length > 0 && tab === 'review' && (
        <div className="mb-3 flex items-center gap-2 text-sm"><span>{selIds.length} selected</span>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7" disabled={busy} onClick={postSelected} data-testid="bank-post-selected">Post selected</Button>
          <Button size="sm" variant="outline" className="h-7" onClick={() => act(axios.post(`${base}/bank-review/batch`, { row_ids: selIds, action: 'exclude' }).then(() => setSel({})))} data-testid="bank-batch-exclude">Exclude selected</Button></div>
      )}
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-auto">
        <table className="w-full text-sm"><thead className="bg-slate-100 dark:bg-slate-800"><tr className="text-left text-slate-500">
          {tab === 'review' && <th className="p-2"><input type="checkbox" onChange={(e) => { const n = {}; if (e.target.checked) data.rows.forEach((r) => { n[r.row_id] = true; }); setSel(n); }} data-testid="bank-sel-all" /></th>}
          <th className="p-2">Date</th><th className="p-2">Bank description</th><th className="p-2 text-right">Spent</th><th className="p-2 text-right">Received</th>
          {tab === 'review' && <><th className="p-2">From/To</th><th className="p-2">Match/Categorize</th><th className="p-2"><Paperclip className="h-3.5 w-3.5" /></th><th className="p-2"><MessageSquare className="h-3.5 w-3.5" /></th></>}
          <th className="p-2">Action</th></tr></thead>
          <tbody data-testid="bank-rows" className="text-slate-900 dark:text-slate-100">{data.rows.map((r) => {
            const d = draft[r.row_id] || {}; const s = r.suggestion || {};
            const hasMatch = s.source === 'match' || s.match_count > 0;
            const mode = rowMode[r.row_id] || (hasMatch ? 'match' : 'categorize');
            const nAtt = (r.attachments || []).length;
            return (
            <tr key={r.row_id} className="border-t border-slate-200 dark:border-slate-800 align-top" data-testid={`bank-row-${r.row_id}`}>
              {tab === 'review' && <td className="p-2"><input type="checkbox" checked={!!sel[r.row_id]} onChange={(e) => setSel({ ...sel, [r.row_id]: e.target.checked })} data-testid={`bank-sel-${r.row_id}`} /></td>}
              <td className="p-2 whitespace-nowrap">{fmtDate(r.date)}</td><td className="p-2">{r.description}</td>
              <td className="p-2 text-right">{r.spent ? money(r.spent) : ''}</td><td className="p-2 text-right">{r.received ? money(r.received) : ''}</td>
              {tab === 'review' && <>
                <td className="p-2 min-w-[11rem]">
                  {editP[r.row_id] ? (
                    <Combo value={d.payee_id || ''} onChange={(v) => setD(r.row_id, 'payee_id', v)} options={[{ id: '', label: r.received ? 'Select customer' : 'Select vendor' }, ...payeeOpts]} onAdd={addPayee} placeholder={r.received ? 'Select customer' : 'Select vendor'} testid={`bank-payee-${r.row_id}`} />
                  ) : (
                    <button className="text-left text-sm text-slate-900 dark:text-slate-100 hover:underline decoration-dotted" onClick={() => setEditP({ ...editP, [r.row_id]: true })} data-testid={`bank-fromto-${r.row_id}`}>
                      {(d.payee_id && payeeName[d.payee_id]) || r.from_to || <span className="text-slate-400">{r.received ? 'Select customer' : 'Select vendor'}</span>}
                    </button>
                  )}
                </td>
                <td className="p-2 min-w-[13rem] space-y-1">
                  {hasMatch && (
                    <div className="inline-flex rounded border border-slate-300 dark:border-slate-600 text-xs overflow-hidden" data-testid={`bank-mc-toggle-${r.row_id}`}>
                      {['match', 'categorize'].map((m) => (
                        <button key={m} onClick={() => setRowMode({ ...rowMode, [r.row_id]: m })} data-testid={`bank-mc-${m}-${r.row_id}`}
                          className={`px-2 py-0.5 ${mode === m ? 'bg-emerald-600 text-white' : 'text-slate-600 dark:text-slate-300'}`}>{m === 'match' ? 'Match' : 'Categorize'}</button>
                      ))}
                    </div>
                  )}
                  {mode === 'match' && hasMatch ? (
                    <button className="block text-xs text-emerald-600 dark:text-emerald-400 hover:underline" onClick={() => openMatch(r)} data-testid={`bank-matchlink-${r.row_id}`}>{s.match_count || 1} match found</button>
                  ) : editC[r.row_id] ? (
                    <Combo value={d.account_id || ''} onChange={(v) => setD(r.row_id, 'account_id', v)} options={[{ id: '', label: 'Select category' }, ...acctOpts]} placeholder="Category" testid={`bank-category-${r.row_id}`} />
                  ) : (
                    <button className="text-left text-sm text-slate-900 dark:text-slate-100 hover:underline decoration-dotted" onClick={() => setEditC({ ...editC, [r.row_id]: true })} data-testid={`bank-catcell-${r.row_id}`}>
                      {(d.account_id && acctName[d.account_id]) || <span className="text-slate-400">Select category</span>}
                    </button>
                  )}
                  {s.source && s.source !== 'import' && s.source !== 'history' && (
                    <div className="text-[10px] uppercase tracking-wide text-slate-500" data-testid={`bank-src-${r.row_id}`}>{s.source}{s.confidence != null ? ` · ${Math.round(s.confidence * 100)}%` : ''}</div>
                  )}
                </td>
                <td className="p-2">
                  <label className="cursor-pointer inline-flex items-center gap-1 text-slate-500 hover:text-emerald-600" title="Attach" data-testid={`bank-attach-${r.row_id}`}>
                    <Paperclip className="h-4 w-4" />{nAtt > 0 && <span className="text-xs">{nAtt}</span>}
                    <input type="file" className="hidden" onChange={(e) => addAttachment(r, e.target.files?.[0])} />
                  </label>
                </td>
                <td className="p-2">
                  <button onClick={() => addComment(r)} title={r.comment || 'Add comment'} data-testid={`bank-comment-${r.row_id}`}
                    className={`${r.comment ? 'text-emerald-600' : 'text-slate-500'} hover:text-emerald-600`}><MessageSquare className="h-4 w-4" /></button>
                </td>
              </>}
              <td className="p-2">
                {tab === 'review' ? (
                  <div className="relative flex items-center">
                    {mode === 'match' && hasMatch ? (
                      <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" onClick={() => openMatch(r)} data-testid={`bank-match-${r.row_id}`}>Match</Button>
                    ) : (
                      <div className="inline-flex">
                        <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 rounded-r-none" disabled={!d.account_id} onClick={() => postRow(r)} data-testid={`bank-post-${r.row_id}`}>Post</Button>
                        <Button size="sm" className="h-7 bg-emerald-700 hover:bg-emerald-800 rounded-l-none px-1" onClick={() => setRowMenu(rowMenu === r.row_id ? '' : r.row_id)} data-testid={`bank-postmenu-${r.row_id}`}><ChevronDown className="h-3.5 w-3.5" /></Button>
                      </div>
                    )}
                    {rowMenu === r.row_id && (
                      <div className="absolute right-0 top-8 z-20 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg text-sm" data-testid={`bank-postmenu-open-${r.row_id}`}>
                        <button className="block w-full text-left px-3 py-1.5 hover:bg-emerald-50 dark:hover:bg-emerald-600/10 disabled:opacity-40" disabled={!d.account_id} onClick={() => { setRowMenu(''); postRow(r); }} data-testid={`bank-menu-post-${r.row_id}`}>Post</button>
                        <button className="block w-full text-left px-3 py-1.5 hover:bg-emerald-50 dark:hover:bg-emerald-600/10" onClick={() => { setRowMenu(''); act(axios.post(`${base}/bank-review/${r.row_id}/exclude`)); }} data-testid={`bank-menu-exclude-${r.row_id}`}>Exclude</button>
                      </div>
                    )}
                  </div>
                ) : (
                  <Button size="sm" variant="outline" className="h-7" onClick={() => act(axios.post(`${base}/bank-review/${r.row_id}/undo`))} data-testid={`bank-undo-${r.row_id}`}>Undo</Button>
                )}
              </td>
            </tr>
          ); })}
          {data.rows.length === 0 && <tr><td colSpan={10} className="p-4 text-center text-slate-500" data-testid="bank-empty">Nothing here.</td></tr>}
          </tbody></table>
      </div>
      <div className="mt-3 flex items-center gap-3 text-sm text-slate-500">
        <span data-testid="bank-total">{first}-{last} of {data.total} · Page {page} of {pages}</span><div className="flex-1" />
        <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</Button>
        <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</Button>
      </div>

      {matchRow && (
        <Modal title={`Match — ${matchRow.description}`} onClose={() => setMatchRow(null)}>
          {cands.length === 0 ? <p className="text-sm text-slate-400" data-testid="match-none">No matching transactions within a few days.</p> : (
            <div className="space-y-2" data-testid="match-cands">{cands.map((c) => (
              <div key={c.txn_id} className="flex items-center justify-between rounded-lg border border-slate-700 p-2 text-sm text-slate-100">
                <span>{c.date} · {c.type} {c.num ? `#${c.num}` : ''} · {money(Math.abs(c.amount))}</span>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act(axios.post(`${base}/bank-review/${matchRow.row_id}/match`, { txn_id: c.txn_id }).then(() => setMatchRow(null)))} data-testid={`match-pick-${c.txn_id}`}>Match</Button>
              </div>
            ))}</div>
          )}
        </Modal>
      )}
      {showRules && <RulesModal base={base} acctOpts={acctOpts} onClose={() => setShowRules(false)} />}
    </div>
  );
}

export function RulesModal({ base, acctOpts, onClose }) {
  const [rules, setRules] = useState([]);
  const [f, setF] = useState({ contains: '', kind: 'expense', account_id: '' });
  const load = () => axios.get(`${base}/bank-rules`).then((r) => setRules(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => { try { await axios.post(`${base}/bank-rules`, f); setF({ contains: '', kind: 'expense', account_id: '' }); load(); } catch (e) { alert(errOf(e)); } };
  const del = async (id) => { await axios.delete(`${base}/bank-rules/${id}`); load(); };
  return (
    <Modal title="Rules" onClose={onClose}>
      <p className="text-sm text-slate-400 mb-3">When a bank description contains a phrase, suggest a category.</p>
      <div className="space-y-2 mb-4" data-testid="rules-list">{rules.map((r) => (
        <div key={r.rule_id} className="flex items-center justify-between rounded-lg border border-slate-700 p-2 text-sm">
          <span>“{r.contains}” → {r.kind} {(acctOpts.find((a) => a.id === r.account_id) || {}).label || ''}</span>
          <button onClick={() => del(r.rule_id)} className="text-red-400 text-xs" data-testid={`rule-del-${r.rule_id}`}>Remove</button>
        </div>
      ))}{rules.length === 0 && <p className="text-xs text-slate-500">No rules yet.</p>}</div>
      <div className="grid grid-cols-3 gap-2 items-end">
        <Field label="Description contains"><Txt value={f.contains} onChange={(e) => setF({ ...f, contains: e.target.value })} data-testid="rule-contains" /></Field>
        <Field label="Category"><Combo value={f.account_id} onChange={(v) => setF({ ...f, account_id: v })} options={acctOpts} testid="rule-account" /></Field>
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={add} data-testid="rule-add">Add rule</Button>
      </div>
    </Modal>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl border border-slate-700 bg-slate-900 p-5" onClick={(e) => e.stopPropagation()} data-testid="tool-modal">
        <div className="flex items-center justify-between mb-3"><h3 className="font-semibold text-slate-100">{title}</h3><button onClick={onClose} className="text-slate-400">✕</button></div>
        {children}
      </div>
    </div>
  );
}

// ══ Online IMPORTER WIZARD ══════════════════════════════════════════════════════
export function OnlineImport({ base, onBack }) {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const [settings, setSettings] = useState({ nonprofit: true, accounting_method: 'cash', fiscal_year_start_month: 7 });
  const [cutover, setCutover] = useState('');
  useEffect(() => { axios.get(`${base}/online-import`).then((r) => { setJob(r.data); if (r.data?.company_settings) setSettings(r.data.company_settings); }).catch(() => {}); }, [base]);
  const call = async (fn) => { setBusy(true); setErr(''); try { const r = await fn; setJob(r.data); return r.data; } catch (e) { setErr(errOf(e)); } finally { setBusy(false); } };
  const start = () => call(axios.post(`${base}/online-import/start`));
  const wid = job?.wid;
  const onFile = async (e) => { const f = e.target.files?.[0]; if (!f || !wid) return; const fd = new FormData(); fd.append('file', f); setBusy(true); setErr(''); try { const r = await axios.post(`${base}/online-import/${wid}/upload`, fd); setJob(r.data); } catch (e2) { setErr(errOf(e2)); } finally { setBusy(false); } };
  const status = job?.status;
  const Step = ({ n, title, done, children }) => (
    <Card className={`mb-3 ${done ? 'border-emerald-700' : ''}`}>
      <div className="flex items-center gap-2 mb-2"><span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs ${done ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-300'}`}>{done ? '✓' : n}</span><span className="font-medium text-slate-100">{title}</span></div>
      {children}
    </Card>
  );
  const files = job?.files || {};
  return (
    <div className="p-6 max-w-3xl" data-testid="online-import-page">
      <PageHead title="Import from online accounting" sub="Upload your Online ‘Export data’ zip, map, dry-run, reconcile, then cut over." onBack={onBack} />
      {err && <div className="mb-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300" data-testid="online-err">{err}</div>}
      {!job ? <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={start} disabled={busy} data-testid="online-start">Start a online accounting import</Button> : (
        <>
          <Step n={1} title="Upload Online export" done={!!files.journal}>
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm cursor-pointer" data-testid="online-upload">
              <Upload className="h-4 w-4" /> Choose .zip or .xlsx<input type="file" accept=".zip,.xlsx,.csv" className="hidden" onChange={onFile} /></label>
            {Object.keys(files).length > 0 && <div className="mt-2 text-xs text-slate-400" data-testid="online-files">Uploaded: {Object.keys(files).join(', ')}</div>}
          </Step>
          <Step n={2} title="Company settings" done={status && status !== 'draft' && status !== 'uploaded'}>
            <div className="grid sm:grid-cols-3 gap-3">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={settings.nonprofit} onChange={(e) => setSettings({ ...settings, nonprofit: e.target.checked })} data-testid="online-nonprofit" /> Nonprofit naming</label>
              <Field label="Accounting method"><Combo value={settings.accounting_method} onChange={(v) => setSettings({ ...settings, accounting_method: v })} options={[{ id: 'cash', label: 'Cash' }, { id: 'accrual', label: 'Accrual' }]} testid="online-method" /></Field>
              <Field label="Fiscal year starts"><Combo value={String(settings.fiscal_year_start_month)} onChange={(v) => setSettings({ ...settings, fiscal_year_start_month: Number(v) })} options={['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => ({ id: String(i + 1), label: m }))} testid="online-fym" /></Field>
            </div>
            <Button variant="outline" className="mt-2" disabled={busy || !wid} onClick={() => call(axios.post(`${base}/online-import/${wid}/settings`, settings))} data-testid="online-save-settings">Save settings</Button>
          </Step>
          <Step n={3} title="Map & dry run" done={!!job?.dry_run}>
            <div className="flex gap-2">
              <Button variant="outline" disabled={busy} onClick={() => call(axios.post(`${base}/online-import/${wid}/map`))} data-testid="online-map">Map</Button>
              <Button variant="outline" disabled={busy} onClick={() => call(axios.post(`${base}/online-import/${wid}/dry-run`))} data-testid="online-dryrun">Dry run</Button>
            </div>
            {job?.map && <div className="mt-2 text-xs text-slate-400" data-testid="online-map-info">{job.map.accounts} accounts · {job.map.journal_txns} transactions{job.map.unresolved_accounts?.length ? ` · ${job.map.unresolved_accounts.length} unresolved` : ' · all resolved'}</div>}
            {job?.dry_run && <div className="mt-1 text-xs text-emerald-400" data-testid="online-dryrun-info">Would post {job.dry_run.posted} transactions, {job.dry_run.needs_review_count} need review.</div>}
          </Step>
          <Step n={4} title="Commit" done={job?.committed}>
            <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy || !job?.dry_run} onClick={() => call(axios.post(`${base}/online-import/${wid}/commit`))} data-testid="online-commit">Commit import</Button>
            {job?.commit_summary && <div className="mt-2 text-xs text-emerald-400" data-testid="online-commit-info">Posted {job.commit_summary.posted} · {job.commit_summary.accounts} accounts · items {job.commit_summary.items}</div>}
          </Step>
          <Step n={5} title="Reconcile" done={job?.reconcile?.all_pass}>
            <Button variant="outline" disabled={busy || !job?.committed} onClick={() => call(axios.post(`${base}/online-import/${wid}/reconcile`))} data-testid="online-reconcile">Run reconciliation</Button>
            {job?.reconcile?.checks?.trial_balance && <div className="mt-2 text-xs text-slate-400" data-testid="online-recon-info">Trial balance: {job.reconcile.checks.trial_balance.diff_count} differences (checked {job.reconcile.checks.trial_balance.checked}).</div>}
          </Step>
          <Step n={6} title="Cut over" done={status === 'live'}>
            <div className="flex gap-2 items-end"><Field label="Cut-over date"><Txt type="date" value={cutover} onChange={(e) => setCutover(e.target.value)} data-testid="online-cutover-date" /></Field>
              <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy || !job?.committed || !cutover} onClick={() => call(axios.post(`${base}/online-import/${wid}/cutover`, { cutover_date: cutover }))} data-testid="online-cutover">Cut over</Button></div>
            {status === 'live' && <div className="mt-2 text-sm text-emerald-400">Import is live — closing date set to {job.closing_date}.</div>}
          </Step>
        </>
      )}
    </div>
  );
}

// ══ ACCOUNTING SETTINGS (default posting accounts) ═══════════════════════════
const DEFAULT_KEYS = [['bank_service_charge', 'Bank service charge'], ['finance_charge', 'Finance charge'],
  ['interest_earned', 'Interest earned'], ['reconciliation_discrepancy', 'Reconciliation discrepancies'],
  ['uncategorized_income', 'Uncategorized income'], ['uncategorized_expense', 'Uncategorized expense'],
  ['discount', 'Discount account']];

export function AccountingSettings({ base, onBack }) {
  const [accounts, setAccounts] = useState([]);
  const [da, setDa] = useState({});
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState('');
  useEffect(() => {
    axios.get(`${base}/accounts`).then((r) => setAccounts((r.data?.accounts || []).filter((a) => a.is_active))).catch(() => {});
    axios.get(`${base}/settings`).then((r) => setDa(r.data?.default_accounts || {})).catch(() => {});
  }, [base]);
  const opts = accounts.map((a) => ({ id: a.account_id, label: a.full_name || a.name }));
  const save = async () => { setBusy(true); setMsg(''); try { await axios.put(`${base}/settings/default-accounts`, { default_accounts: da }); setMsg('Saved.'); } catch (e) { setMsg(errOf(e)); } finally { setBusy(false); } };
  return (
    <div className="p-6 max-w-2xl" data-testid="accounting-settings-page">
      <PageHead title="Accounting settings" sub="Choose the default account each automatic posting uses." onBack={onBack} />
      <Card>
        <div className="space-y-3">{DEFAULT_KEYS.map(([k, l]) => (
          <Field key={k} label={l}><Combo value={da[k] || ''} onChange={(v) => setDa({ ...da, [k]: v })} options={[{ id: '', label: '— default —' }, ...opts]} testid={`da-${k}`} /></Field>
        ))}</div>
        <div className="mt-4 flex items-center gap-3"><Button className="bg-emerald-600 hover:bg-emerald-700" onClick={save} disabled={busy} data-testid="da-save">{busy ? 'Saving…' : 'Save'}</Button>{msg && <span className="text-sm text-slate-300" data-testid="da-msg">{msg}</span>}</div>
      </Card>
    </div>
  );
}
